/**
 * O relógio da sessão no cliente (docs/spec/session-clock.md): o que a pílula do chat, o anel do
 * botão da mesa e as linhas do chat mostram. Puro: quem desenha passa a hora do servidor
 * (`serverNow()`) e o `t` do idioma.
 */
import { gameSessionTimes, isGameSessionOpen, nextGameSessionNumber, type GameSession, type GameSessionStep } from '../../shared.js';

export type Translate = (key: string, params?: Record<string, string | number>) => string;

const HOUR = 3_600_000;

/** O relógio de uma mesa aberta: a última sessão dela e se eu vi o fim dessa sessão ao vivo. */
export type RoomClock = {
  session: GameSession | null;
  /** a sessão terminou enquanto eu estava na mesa: a pílula mostra o resumo até a próxima */
  endedLive: boolean;
};

/**
 * O relógio depois de um estado novo. `live`: o estado chegou agora (`gameSession.state`), e não no
 * `ready`. O resumo do fim fica para quem viu o fim: reconectar não o tira (é a mesma sessão), e
 * quem entra depois não o vê.
 */
export function nextClock(prev: RoomClock | null | undefined, session: GameSession | null, live: boolean): RoomClock {
  const before = prev?.session ?? null;
  const same = !!session && !!before && before.id === session.id;
  const endedLive = session?.status === 'ended' && same && (prev!.endedLive || (live && before.status !== 'ended'));
  return { session, endedLive };
}

/** O que a pílula mostra. */
export type PillView =
  | { kind: 'none' }
  /** um mestre, sem sessão aberta: começar a próxima */
  | { kind: 'start'; number: number }
  | { kind: 'open'; session: GameSession; paused: boolean; controls: boolean }
  /** acabou de terminar (vi ao vivo); `next`: o mestre já pode começar a próxima */
  | { kind: 'ended'; session: GameSession; next: number | null };

/** `enabled`: o servidor tem o relógio; `admin`: eu administro a mesa. */
export function pillView(clock: RoomClock | null | undefined, admin: boolean, enabled: boolean): PillView {
  if (!enabled) return { kind: 'none' };
  const session = clock?.session ?? null;
  if (isGameSessionOpen(session)) return { kind: 'open', session, paused: session.status === 'paused', controls: admin };
  if (session && clock?.endedLive) return { kind: 'ended', session, next: admin ? nextGameSessionNumber(session) : null };
  return admin ? { kind: 'start', number: nextGameSessionNumber(session) } : { kind: 'none' };
}

/** O anel do botão da mesa: só com a sessão aberta. `progress`: quanto da hora de jogo já correu (0 a 1). */
export function ringView(clock: RoomClock | null | undefined, now: number): { number: number; paused: boolean; playedMs: number; progress: number } | null {
  const session = clock?.session ?? null;
  if (!isGameSessionOpen(session)) return null;
  const { playedMs } = gameSessionTimes(session, now);
  return { number: session.number, paused: session.status === 'paused', playedMs, progress: (playedMs % HOUR) / HOUR };
}

/** O ponto de onde o jogo conta (jogo = agora − âncora), para o tique cair na virada do segundo. */
export function playAnchor(session: GameSession): number {
  return session.since - session.playedMs;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "1:24:37": sempre com as horas, para a largura não pular na primeira hora. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** Uma duração em texto, para baixo: "3h30", "3h", "12 min", "menos de 1 min". */
export function formatDuration(t: Translate, ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return t('clock.duration.under');
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return t('clock.duration.minutes', { m });
  return m ? t('clock.duration.hoursMinutes', { h, mm: pad(m) }) : t('clock.duration.hours', { h });
}

/** Os minutos do anel no botão da mesa, curtos para caber nos 64 px: "24 min", "1h24", "2h". */
export function formatRingTime(t: Translate, ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return t('clock.duration.minutes', { m });
  return m ? t('clock.duration.hoursMinutes', { h, mm: pad(m) }) : t('clock.duration.hours', { h });
}

/** Como as horas aparecem (os testes passam formatos fixos; a tela usa os do idioma da página). */
export type ClockFormats = { time: (ms: number) => string; day: (ms: number) => string };

export const LOCAL_FORMATS: ClockFormats = {
  time: (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  day: (ms) => new Date(ms).toLocaleDateString([], { day: '2-digit', month: '2-digit' }),
};

/** "às 19:05", ou "em 27/10, às 23:12" quando o passo valeu noutro dia que não o da linha. */
function when(t: Translate, at: number, lineAt: number, formats: ClockFormats): string {
  const time = formats.time(at);
  return formats.day(at) === formats.day(lineAt) ? t('clock.at', { time }) : t('clock.atDay', { day: formats.day(at), time });
}

/** O texto da linha do chat de um passo do relógio, no idioma e no fuso de quem lê. */
export function sessionLineText(t: Translate, step: GameSessionStep, lineAt: number, formats: ClockFormats = LOCAL_FORMATS): string {
  const at = when(t, step.at, lineAt, formats);
  switch (step.kind) {
    case 'start':
      return t('chat.event.session.start', { n: step.number, when: at });
    case 'pause':
      return t(step.auto ? 'chat.event.session.pause.auto' : 'chat.event.session.pause', { n: step.number, when: at });
    case 'resume':
      return t('chat.event.session.resume', { n: step.number, when: at, pause: formatDuration(t, step.pauseMs) });
    case 'end': {
      const line = t('chat.event.session.end', { n: step.number, when: at, played: formatDuration(t, step.playedMs) });
      return step.pausedMs > 0 ? line + t('chat.event.session.end.paused', { paused: formatDuration(t, step.pausedMs) }) : line;
    }
  }
}

/** As pausas, contando a que está em curso: " · 1 pausa, 12 min" (vazio sem pausas). */
export function pausesText(t: Translate, session: GameSession, now: number): string {
  const { pausedMs, pauseMs } = gameSessionTimes(session, now);
  const pauses = session.pauses + (session.status === 'paused' ? 1 : 0);
  if (!pauses) return '';
  return t(pauses === 1 ? 'clock.details.pauses.one' : 'clock.details.pauses.many', { count: pauses, paused: formatDuration(t, pausedMs + pauseMs) });
}

/** A dica da pílula: "Sessão 13 · começou às 19:05 · 1 pausa, 12 min". */
export function clockDetails(t: Translate, session: GameSession, now: number, formats: ClockFormats = LOCAL_FORMATS): string {
  return t('clock.details', { n: session.number, time: formats.time(session.startedAt) }) + pausesText(t, session, now);
}
